import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useForm, SubmitHandler, UseFormRegister } from "react-hook-form";

import alertCircle from "@/assets/images/alert-circle.svg";

import { Button } from "@/components/ui/button";
import NeedHelpButton from "@/components/need-help-button/need-help-button";
import Form from "@/components/Form/Form";
import Header from "@/components/header/Header";
import Footer from "@/components/footer/Footer";
import Identifier from "@/components/identifier-input/IdentifierInput";

import { FormError } from "@/models/continente-credentials/common/FormError";
import { IdentifierValidation } from "@/models/continente-credentials/loyaltycard/IdentifierValidation";
import { LoyaltyCardAssociationResponse } from "@/models/shared/loyalty/LoyaltyCardAssociationResponse";

import { loyaltyCardService } from "@/services/continente-credentials/loyaltycard/LoyaltyCardService";
import { tryCatch } from "@/utils/try-catch";
import { useApiResponseHandler } from "@/hooks/useApiResponseHandler";
import ButtonContainer from "@/components/button-container";

type Form = IdentifierValidation & FormError;

export default function IdentificationVerification() {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const location = useLocation<LoyaltyCardAssociationResponse>();
  const requestedValues: number[] = location.state?.properties?.requestedIdentifierValues;

  const { handleResponse } = useApiResponseHandler();

  const formState = useForm<Form>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    clearErrors,
    formState: { errors },
  } = formState;

  setValue("requestedIdentifierValues", requestedValues);

  const handleIdentifierChange = (value: string[]) => {
    clearErrors();
    setValue("identifierValues", value);
  };

  const onSubmit: SubmitHandler<IdentifierValidation> = async (
    identifierValidation
  ) => {
    setLoading(true);
    const { data: response, error } = await tryCatch(loyaltyCardService.validateIdentifier(
      identifierValidation,
      location.state != null ? location.state.nextStep : ""
    ));

    handleResponse(response, {
      setError
    }, error);

    setLoading(false);
  };

  useEffect(() => {
    const identifierValidator = (register: UseFormRegister<Form>) =>
      register("identifierValues", {
        required: {
          value: true,
          message: t("common.required_field"),
        },
        pattern: {
          value: /^[0-9]*$/,
          message: t("identification_verification.identifier_must_be_numeric"),
        },
      });
    identifierValidator(register);
  }, [register, t]);

  return (
    <Form
      id="IdentificationVerification"
      form={formState}
      autoComplete="off"
      onSubmit={(e) => {
        clearErrors();
        handleSubmit(onSubmit)(e);
      }}
    >
      <Header
        title={t("identification_verification.header_title")}
        subtitle={t("identification_verification.header_message")}
        showLoading={loading}
        previousStepBackXTimes={1}
      />
      <Identifier
        requestedValues={requestedValues}
        onChange={handleIdentifierChange}
      />
      {errors.submitError && (
        <div className="inline-flex align-items-center justify-center w-100">
          <>
            <img src={alertCircle} alt="" className="mt-2 h-8 w-8" />
            <span className="text-[var(--redCredentials)] text-xs ml-2 mt-2">{errors.submitError.message}</span>
          </>
        </div>
      )}
      <ButtonContainer>
        <Button>{t("identification_verification.button_next_message")}</Button>
        <NeedHelpButton />
      </ButtonContainer>
      <div className="flex flex-col">
        <Footer
          contextualInformationVisible={false}
          footerAppsWebsitesIconsVisible={false}
        />
      </div>
    </Form>
  );
}
